/**
 * Funnel Utilities
 * Common utility functions for funnel-related operations across the application.
 * 
 * This is the single source of truth for funnel ID detection logic.
 * Used by: FunnelContext, promoManager, App.jsx, and other components.
 */

import { sessionStore } from './sessionKey'

/** sessionStorage — user intentionally backed out of checkout to contact/quiz */
export const CV_CHECKOUT_USER_BACKED_KEY = 'cv_checkout_user_backed'
/** localStorage — marks the browser that started checkout flow for a case */
export const CV_IN_FLOW_ACTIVE_KEY = 'cv_inflow_active'
export const CV_IN_FLOW_CASE_ID_KEY = 'cv_inflow_case_id'

/**
 * @param {string} pathname
 * @returns {boolean}
 */
export const isCheckoutUrlPath = (pathname = '') =>
  pathname.includes('/checkout/selection') || pathname.includes('/checkout/payment')

/**
 * @param {string} pathname
 * @param {string} routeUrl - e.g. '/cv-1', '/nad'
 * @returns {boolean}
 */
export const isQuizBaseRoutePath = (pathname = '', routeUrl = '') =>
  pathname === routeUrl || pathname === `${routeUrl}/`

/**
 * Resolve which funnel step to render when URL and persisted step disagree
 * (e.g. browser back from checkout leaves step=checkout while URL is /cv-1).
 *
 * @param {Object} params
 * @param {string} params.pathname
 * @param {number} params.currentStep
 * @param {Object} params.config - funnel config with steps + routeUrl
 * @param {string} params.contactInfoStepId
 * @param {string} params.checkoutStepId
 * @returns {number}
 */
export const resolveEffectiveFunnelStep = ({
  pathname,
  currentStep,
  config,
  contactInfoStepId,
  checkoutStepId,
}) => {
  if (!config?.steps) return currentStep

  const checkoutStepIndex = config.steps.findIndex((s) => s.id === checkoutStepId)
  const contactInfoStepIndex = config.steps.findIndex((s) => s.id === contactInfoStepId)
  if (checkoutStepIndex === -1 || contactInfoStepIndex === -1) return currentStep

  if (isCheckoutUrlPath(pathname)) {
    if (sessionStore.getItem(CV_CHECKOUT_USER_BACKED_KEY, config) === 'true') {
      return contactInfoStepIndex
    }
    return currentStep
  }

  const routeUrl = config.routeUrl || ''
  if (
    isQuizBaseRoutePath(pathname, routeUrl) &&
    currentStep === checkoutStepIndex &&
    sessionStore.getItem('cv_checkout_active', config) === 'true'
  ) {
    return contactInfoStepIndex
  }

  return currentStep
}

/**
 * Checkout pages auto-rewrite /cv-1 → /cv-1/checkout/selection on mount.
 * Skip that when the user backed out of checkout (browser back).
 *
 * @param {string} pathname
 * @param {Object} config
 * @returns {boolean}
 */
export const shouldBlockCheckoutUrlRewrite = (pathname, config) => {
  if (isCheckoutUrlPath(pathname)) return false

  const routeUrl = config?.routeUrl || ''
  if (!isQuizBaseRoutePath(pathname, routeUrl)) return false

  return sessionStore.getItem('cv_checkout_active', config) === 'true'
}

/**
 * @param {Object} config
 */
export const markCheckoutUserBacked = (config) => {
  sessionStore.setItem(CV_CHECKOUT_USER_BACKED_KEY, 'true', config)
}

/**
 * @param {Object} config
 */
export const clearCheckoutNavigationFlags = (config) => {
  sessionStore.removeItem('cv_checkout_active', config)
  sessionStore.removeItem(CV_CHECKOUT_USER_BACKED_KEY, config)
}

/**
 * Persist browser-scoped in-flow marker for cross-browser guard decisions.
 *
 * @param {string} caseId
 */
export const markInFlowBrowserForCase = (caseId) => {
  if (!caseId || typeof window === 'undefined') return
  window.localStorage.setItem(CV_IN_FLOW_ACTIVE_KEY, 'true')
  window.localStorage.setItem(CV_IN_FLOW_CASE_ID_KEY, String(caseId))
}

/**
 * @returns {boolean}
 */
export const hasInFlowBrowserCaseMatch = (caseId) => {
  if (!caseId || typeof window === 'undefined') return false
  const active = window.localStorage.getItem(CV_IN_FLOW_ACTIVE_KEY) === 'true'
  const storedCaseId = window.localStorage.getItem(CV_IN_FLOW_CASE_ID_KEY) || ''
  return active && storedCaseId === String(caseId)
}

export const clearInFlowBrowserMarker = () => {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(CV_IN_FLOW_ACTIVE_KEY)
  window.localStorage.removeItem(CV_IN_FLOW_CASE_ID_KEY)
}

/**
 * @param {Object} config
 */
export const clearCheckoutUserBackedFlag = (config) => {
  sessionStore.removeItem(CV_CHECKOUT_USER_BACKED_KEY, config)
}

/**
 * Get funnel ID from URL pathname using simple path matching.
 * This is a lightweight version that doesn't require funnel configs.
 * Used when funnel configs are not available (e.g., in utility functions, early app initialization).
 * 
 * @param {string} pathname - The URL pathname to check
 * @returns {string|null} The funnel ID (e.g., 'variant-cv-1', 'variant-cv-2', 'variant-cv-nad') or null if not found
 * 
 * @example
 * getFunnelIdFromPath('/cv-nad/checkout') // returns 'variant-cv-nad'
 * getFunnelIdFromPath('/cv-1/quiz') // returns 'variant-cv-1'
 * getFunnelIdFromPath('/cv-3/quiz') // returns 'variant-cv-3'
 * getFunnelIdFromPath('/unknown') // returns null (404 should be shown)
 */
export const getFunnelIdFromPath = (pathname = '') => {
  if (typeof pathname !== 'string') return null
  if (pathname.startsWith('/nad')) return 'variant-cv-nad'
  if (pathname.startsWith('/sermorelin')) return 'variant-cv-sermorelin'
  if (pathname.startsWith('/cv-2')) return 'variant-cv-2'
  if (pathname.startsWith('/cv-3')) return 'variant-cv-3'
  if (pathname.startsWith('/cv-4')) return 'variant-cv-4'
  if (pathname.startsWith('/cv-1')) return 'variant-cv-1'
  return null
}

/**
 * Get funnel ID from pathname using loaded funnel configurations.
 * This is the more robust, config-driven version used by FunnelContext.
 * Dynamically checks all loaded configs to find matching routeUrl.
 * 
 * @param {string} pathname - The URL pathname to check
 * @param {Object} funnelConfigs - Object containing funnel configurations
 * @returns {string|null} The funnel ID or null if no match found
 * 
 * @example
 * getFunnelIdFromPathWithConfigs('/cv-nad/checkout', configs) // returns 'variant-cv-nad'
 * getFunnelIdFromPathWithConfigs('/unknown', configs) // returns null (404 should be shown)
 */
export const getFunnelIdFromPathWithConfigs = (pathname, funnelConfigs) => {
  // Check all loaded configs to find matching routeUrl
  for (const [funnelId, config] of Object.entries(funnelConfigs)) {
    if (config.routeUrl && pathname.includes(config.routeUrl)) {
      return funnelId
    }
  }
  return null
}
